import { defaultFormDesign, parseFormDesign } from './data/form-design';

export const clone = value => JSON.parse(JSON.stringify(value));
export function newForm(template) {
  return {
    version: 2,
    design: parseFormDesign(
      Object.assign(defaultFormDesign(), clone(template.design))
    ),
    fields: clone(template.fields),
    behavior: {
      lists: [],
      tags: [],
      sequences: [],
      recipients: [],
      defaultAccountEmail: false,
      optin: 'single',
      successMessage: 'Thank you! Your response has been received.',
      redirectUrl: '',
      confirmSenderId: '',
      confirmSubject: 'Please confirm your email address',
      confirmPreviewText: '',
      confirmHtml:
        '<p>Thanks for signing up.</p><p><a href="{optin_confirmation_link}">Confirm your email address</a></p>',
      welcomeEnabled: false,
      welcomeSenderId: '',
      welcomeSubject: 'Welcome!',
      welcomePreviewText: '',
      welcomeHtml: '<p>Thanks for joining us, {first_name}.</p>'
    }
  };
}
export function errorMessage(error) {
  const data = error && error.response && error.response.data;
  if (data && data.errors)
    return Object.values(data.errors)
      .reduce((all, item) => all.concat(item), [])
      .join(' ');
  return (
    (data && (data.error || data.message)) ||
    error.message ||
    'Unable to complete this request. Please try again.'
  );
}
export const safeUrl = value =>
  /^(https?:\/\/|mailto:|tel:|\{[a-zA-Z_{}]+\}|#)/i.test(String(value || ''));
export function validateEmailCss(css) {
  const decoded = css.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\\([0-9a-f]{1,6})\s?|\\(.)/gi,
    (match, hex, character) => hex ? String.fromCodePoint(parseInt(hex, 16) || 65533) : character);
  if (/expression\s*\(|javascript\s*:|behavior\s*:|-moz-binding|@import/i.test(decoded))
    throw new Error('Remove active CSS from the email.');
  const remaining = decoded.replace(/url\s*\(\s*(?:"([^"\r\n]*)"|'([^'\r\n]*)'|([^\s)'";]*))\s*\)/gi,
    (match, double, single, bare) => {
      const url = double || single || bare || '';
      if (!/^https?:\/\/[^\s<>"']+$/i.test(url)) throw new Error('Use HTTP or HTTPS URLs for email backgrounds and fonts.');
      return '';
    });
  if (/url\s*\(/i.test(remaining)) throw new Error('Check the email background URL.');
}
export function validateTemplateContent(html) {
  if (typeof html !== 'string' || !/<[a-z][\s\S]*>/i.test(html))
    throw new Error('Add email content before saving the template.');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('head,style,script,template').forEach(node => node.remove());
  if ((doc.body.textContent || '').replace(/[\s\u00a0\u200b-\u200f\ufeff]/g, '')) return;
  if (Array.from(doc.body.querySelectorAll('*')).some(node =>
    (node.tagName === 'IMG' && (node.getAttribute('src') || '').trim()) ||
    (node.getAttribute('background') || '').trim() ||
    /background(?:-image)?\s*:[^;]*url\(\s*['"]?https?:\/\//i.test(node.getAttribute('style') || '')
  )) return;
  throw new Error('Add email content before saving the template.');
}

export function validateEmailHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (
    doc.querySelector(
      'script,iframe,object,embed,svg,math,form,input,button,textarea,select,base,link,meta[http-equiv]'
    )
  )
    throw new Error(
      'Email HTML cannot contain scripts, forms or embedded applications.'
    );
  for (const el of doc.querySelectorAll('*')) {
    for (const attr of el.attributes) {
      if (/^on|^srcdoc$/i.test(attr.name))
        throw new Error('Remove executable HTML attributes.');
      if (
        /^(href|src|background|xlink:href)$/i.test(attr.name) &&
        attr.value &&
        !safeUrl(attr.value.replace(/[\u0000-\u0020]+/g, ''))
      )
        throw new Error(
          'Use a valid HTTP, HTTPS, email, telephone or merge-variable link.'
        );
      if (attr.name === 'style') validateEmailCss(attr.value);
    }
  }
  for (const el of doc.querySelectorAll('style')) validateEmailCss(el.textContent);
  return html;
}
// Raw imported templates stay exact. Convert variables only when applying a copy to a message.
export function grooveEmail(html, companyName = '') {
  if (/data-groove-needs-link/i.test(html))
    throw new Error('Add the website or form link in the Design tab before using this email.');
  const escapedCompany = companyName
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const result = html.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (token, key) => {
    if (
      [
        'first_name',
        'last_name',
        'email',
        'phone_number',
        'company',
        'address',
        'city',
        'state',
        'zip',
        'country'
      ].includes(key)
    )
      return `{${key}}`;
    if (key === 'company_name' && companyName.trim()) return escapedCompany;
    if (key === 'current_date')
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    throw new Error(
      key === 'company_name'
        ? 'Enter your company name before using this template.'
        : `Replace the ${token} placeholder before using this template.`
    );
  });
  return validateEmailHtml(result);
}
export function previewDocument(html) {
  // No scripts or parent access, even if the source is intentionally invalid while typing.
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('meta[http-equiv],base').forEach(node => node.remove());
  const policy = doc.createElement('meta');
  policy.setAttribute('http-equiv', 'Content-Security-Policy');
  policy.setAttribute('content', "default-src 'none'; img-src https: http: data:; style-src 'unsafe-inline'; font-src https:; form-action 'none'; base-uri 'none'");
  doc.head.insertBefore(policy, doc.head.firstChild);
  if (!doc.head.querySelector('meta[name="viewport"]')) {
    const viewport = doc.createElement('meta');
    viewport.name = 'viewport'; viewport.content = 'width=device-width,initial-scale=1';
    doc.head.appendChild(viewport);
  }
  if (!doc.body.style.margin) doc.body.style.margin = '0';
  return '<!doctype html>' + doc.documentElement.outerHTML;
}
export function publicFormUrl(id) {
  const base =
    (window.config && window.config.grooveappsurl) || window.location.origin;
  return (
    base.replace(/\/$/, '') +
    '/groovemail/studioForm.html?id=' +
    encodeURIComponent(id)
  );
}
export function embedMarkup(id, name) {
  const title = String(name).replace(
    /[&<>"']/g,
    character =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[
        character
      ])
  );
  return `<iframe src="${publicFormUrl(
    id
  )}" title="${title}" width="100%" height="760" style="border:0;max-width:640px" loading="lazy"></iframe>`;
}
